import L from 'leaflet'
import { t } from 'i18next'
import { Feature, MultiPolygon, Polygon } from '@turf/helpers'
import booleanWithin from '@turf/boolean-within'
import intersect from '@turf/intersect'
import WeatherBarRain from './img/WeatherBarRain.png'
import WeatherBarMixed from './img/WeatherBarMixed.png'
import WeatherBarSnow from './img/WeatherBarSnow.png'
import { WeatherZoneName } from './types'

interface Props {
  map: L.Map
  legendLayer: L.Layer
  legend: boolean
  zone: WeatherZoneName
}

interface WeatherZone {
  name: string
  zone: Feature<Polygon | MultiPolygon | any>
}

const weatherLegend = [
  [WeatherBarRain, 'TRACKER.MAP_PART.WEATHER_RAIN'],
  [WeatherBarMixed, 'TRACKER.MAP_PART.WEATHER_MIXED'],
  [WeatherBarSnow, 'TRACKER.MAP_PART.WEATHER_SNOW']]

export const globalWeatherZone: WeatherZone = {
  name: 'Global24_SinglePicture_-180_-65',
  zone: L.rectangle([[-65, -180], [65, 180]]).toGeoJSON()
}

export const conusZone: WeatherZone = {
  name: 'Conus',
  zone: L.rectangle([[22.5, -127], [52, -65]]).toGeoJSON()
}

export const canadaZone: WeatherZone = {
  name: 'Canada',
  zone: L.rectangle([[42, -127], [58, -50]]).toGeoJSON()
}

const americaBounds = L.rectangle([[22.5, -127], [58, -50]]).toGeoJSON()

export const weatherZones: WeatherZone[] = [
  { name: 'Global24_-180_-65', zone: L.rectangle([[-65, -180], [-65, -120]]).toGeoJSON() },
  { name: 'Global24_-120_-65', zone: L.rectangle([[-65, -120], [-32.5, -60]]).toGeoJSON() },
  { name: 'Global24_-60_-65', zone: L.rectangle([[-65, -60], [-32.5, 0]]).toGeoJSON() },
  { name: 'Global24_0_-65', zone: L.rectangle([[-65, 0], [-32.5, 60]]).toGeoJSON() },
  { name: 'Global24_60_-65', zone: L.rectangle([[-65, 60], [-32.5, 120]]).toGeoJSON() },
  { name: 'Global24_120_-65', zone: L.rectangle([[-65, 120], [-32.5, 180]]).toGeoJSON() },
  { name: 'Global24_-180_-32.5', zone: L.rectangle([[-32.5, -180], [0, -120]]).toGeoJSON() },
  { name: 'Global24_-120_-32.5', zone: L.rectangle([[-32.5, -120], [0, -60]]).toGeoJSON() },
  { name: 'Global24_-60_-32.5', zone: L.rectangle([[-32.5, -60], [0, 0]]).toGeoJSON() },
  { name: 'Global24_0_-32.5', zone: L.rectangle([[-32.5, 0], [0, 60]]).toGeoJSON() },
  { name: 'Global24_60_-32.5', zone: L.rectangle([[-32.5, 60], [0, 120]]).toGeoJSON() },
  { name: 'Global24_120_-32.5', zone: L.rectangle([[-32.5, 120], [0, 180]]).toGeoJSON() },
  { name: 'Global24_-180_0', zone: L.rectangle([[0, -180], [32.5, -120]]).toGeoJSON() },
  { name: 'Global24_-120_0', zone: L.rectangle([[0, -120], [32.5, -60]]).toGeoJSON() },
  { name: 'Global24_-60_0', zone: L.rectangle([[0, -60], [32.5, 0]]).toGeoJSON() },
  { name: 'Global24_0_0', zone: L.rectangle([[0, 0], [32.5, 60]]).toGeoJSON() },
  { name: 'Global24_60_0', zone: L.rectangle([[0, 60], [32.5, 120]]).toGeoJSON() },
  { name: 'Global24_120_0', zone: L.rectangle([[0, 120], [32.5, 180]]).toGeoJSON() },
  { name: 'Global24_-180_32.5', zone: L.rectangle([[32.5, -180], [65, -120]]).toGeoJSON() },
  { name: 'Global24_-120_32.5', zone: L.rectangle([[32.5, -120], [65, -60]]).toGeoJSON() },
  { name: 'Global24_-60_32.5', zone: L.rectangle([[32.5, -60], [65, 0]]).toGeoJSON() },
  { name: 'Global24_0_32.5', zone: L.rectangle([[32.5, 0], [65, 60]]).toGeoJSON() },
  { name: 'Global24_60_32.5', zone: L.rectangle([[32.5, 60], [65, 120]]).toGeoJSON() },
  { name: 'Global24_120_32.5', zone: L.rectangle([[32.5, 120], [65, 180]]).toGeoJSON() },
  globalWeatherZone
]

const refreshDelay = 1000 * 60 * 15

interface WeatherLinks {
  [key: string]: string
}

export class FlightViewMapLibWeather {
  zone: WeatherZoneName
  globalZoomThreshold = 5

  // leaflet
  map: L.Map
  legendLayer: L.Layer
  weatherControl: L.Control
  weatherOverlays: { [key: string]: L.ImageOverlay } = {}
  weatherLinks: WeatherLinks
  lastUpdate: number = null

  constructor ({ map, legendLayer, legend, zone }: Props) {
    this.map = map
    this.legendLayer = legendLayer
    const bounds = L.rectangle(this.map.getBounds()).toGeoJSON()
    this.zone = zone || (booleanWithin(bounds, americaBounds) ? 'conus' : 'global')
    this.updateWeather().then(() => {
      this.map.on('moveend', this.updateWeather, this)
      this.scheduleWeatherUpdate()
    }).catch((err) => {
      console.error('Cannot update weather', err)
    })
    if (!legend) return
    // @ts-expect-error
    this.weatherControl = L.control({ position: 'bottomright' })
    this.weatherControl.onAdd = function () {
      const div = L.DomUtil.create('div', 'weather-control')
      const labels = []
      const labelLightHeavy = `<div class="FVLib-weight-label"><span></span><span>${t('TRACKER.MAP_PART.WEATHER_LIGHT')}</span><span>${t('TRACKER.MAP_PART.WEATHER_HEAVY')}</span></div>`
      labels.push(labelLightHeavy)
      for (let i = 0; i < weatherLegend.length; i++) {
        labels.push(`<div class="FVLib-weather-label"><span>${t(weatherLegend[i][1])}</span><img src="${weatherLegend[i][0]}" alt="${t(weatherLegend[i][1])}" /></div>`)
      }

      div.innerHTML = labels.join('')
      return div
    }
    this.map.addControl(this.weatherControl)
    this.map.on('layeradd', this.onLayerAdd, this)
    this.map.on('layerremove', this.onLayerRemove, this)
  }

  async sleep (ms: number) {
    return await new Promise(resolve => setTimeout(resolve, ms))
  }

  scheduleWeatherUpdate () {
    this.sleep(refreshDelay).then(() => {
      // force update of links
      this.lastUpdate = null
      this.updateWeather().then(() => {
        this.scheduleWeatherUpdate()
      })
    })
  }

  async updateWeather () {
    try {
      if (this.lastUpdate === null) {
        this.weatherLinks = await this.retrieveWeatherUrls()
        this.lastUpdate = Date.now()
      }
      if (this.zone === 'conus') {
        this.updateWeatherOverlay(conusZone)
      } else if (this.zone === 'canada') {
        this.updateWeatherOverlay(canadaZone)
      } else if (this.zone === 'global' && this.map.getZoom() < this.globalZoomThreshold) {
        for (const overlayKey of Object.keys(this.weatherOverlays)) {
          if (overlayKey !== globalWeatherZone.name) {
            this.weatherOverlays[overlayKey].remove()
          }
        }
        this.updateWeatherOverlay(globalWeatherZone)
      } else {
        this.weatherOverlays[globalWeatherZone.name]?.remove()
        const bounds = L.rectangle(this.map.getBounds()).toGeoJSON()
        for (const weather of weatherZones) {
          if (weather.name === globalWeatherZone.name) continue
          if (intersect(bounds, weather.zone)) {
            this.updateWeatherOverlay(weather)
          }
        }
      }
    } catch (err) {
      console.warn('Cannot update weather overlay', err)
    }
  }

  updateWeatherOverlay (weather: WeatherZone) {
    let overlay = this.weatherOverlays[weather.name]
    if (overlay) {
      const url = this.weatherLinks[weather.name]
      if (overlay._url !== url) {
        overlay.setUrl(this.weatherLinks[weather.name])
      }
      overlay.addTo(this.map)
    } else {
      overlay = this.createWeatherOverlay(new L.GeoJSON(weather.zone).getBounds(), weather.name)
      if (overlay) {
        overlay.addTo(this.map)
        this.weatherOverlays[weather.name] = overlay
      }
    }
  }

  async retrieveWeatherUrls (): Promise<WeatherLinks> {
    const result: Partial<WeatherLinks> = {}
    const wxids = this.zone === 'conus' ? conusZone.name : this.zone === 'canada' ? canadaZone.name : weatherZones.map((zone) => zone.name).join(',')
    const response = await fetch(`https://tracker.flightview.com/FVAccess3/tools/dhtml/proxy/latestradar.aspx?wxid=${wxids}&cb=${Date.now()}`)
    const text = await response.text()
    const lines = text.split('\n')
    for (const line of lines) {
      if (line === '') continue
      const split = line.split(':')
      if (split[2]?.includes(',')) {
        result[split[0]] = `https://tracker.flightview.com/${split[1]}${split[2].split(',')[0]}`
      } else {
        result[split[0]] = `https://tracker.flightview.com/${split[1]}${split[2]}`
      }
    }
    return result
  }

  createWeatherOverlay (bounds: L.LatLngBounds, type: string): L.ImageOverlay {
    // todo: bring anti-meridian fix back for this
    const url = this.weatherLinks[type]
    return url ? L.imageOverlay(url, bounds, { className: 'FVLib-weather-image' }) : null
  }

  onLayerAdd (event: L.LayerEvent) {
    if (event.layer === this.legendLayer) {
      this.map.addControl(this.weatherControl)
    }
  }

  onLayerRemove (event: L.LayerEvent) {
    if (event.layer === this.legendLayer) {
      this.map.removeControl(this.weatherControl)
    }
  }
}
